import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  GenerateUrlRequestBody,
  Paginated,
  PaginationQuery,
  UpdateOrderRequestBody,
  UpdateVisibilityRequestBody,
} from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  BallastType,
  InsertBallastTypeBody,
  UpdateBallastTypeNewImageBody,
  UpdateBallastTypeRemoveImageBody,
} from "./types/ballastTypes";

export const useBallastTypesApi = () => {
  const api = useApi();

  const getAdminBallastType = useCallback(
    (id: string, signal: AbortSignal) => {
      const url = `/admin/settings/border/ballast_types/${id}`;
      return useSWRMutation<BallastType, CustomError, string>(url, (url) => api.get<BallastType>({ url, signal }));
    },
    [api],
  );

  const useAdminBallastTypes = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/settings/border/ballast_types", query);
      return useSWRImmutable<Paginated<BallastType>, CustomError, string>(url, (url) =>
        api.get<Paginated<BallastType>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminBallastTypes = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/settings/border/ballast_types", query);
      return api.get<Paginated<BallastType>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminBallastType = useCallback(
    async (body: InsertBallastTypeBody): Promise<{ id: string }> => {
      const url = `/admin/settings/border/ballast_types`;
      const id = await api.post<{ id: string }, InsertBallastTypeBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminBallastType = useCallback(
    async (
      ballastTypeId: string,
      body: UpdateBallastTypeRemoveImageBody | UpdateBallastTypeNewImageBody,
    ): Promise<void> => {
      const url = `/admin/settings/border/ballast_types/${ballastTypeId}`;
      (await api.put<UpdateBallastTypeRemoveImageBody | UpdateBallastTypeNewImageBody>(url, body),
        mutateCache(["/admin/settings/border/ballast_types"]));
    },
    [api],
  );

  const updateAdminBallastTypeVisibility = useCallback(
    async (ballastTypeId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/border/ballast_types/${ballastTypeId}/visibility`;
      (await api.put<UpdateVisibilityRequestBody>(url, visibility),
        mutateCache(["/admin/settings/border/ballast_types"]));
    },
    [api],
  );

  const updateAdminBallastTypeOrder = useCallback(
    async (ballastTypeId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/border/ballast_types/${ballastTypeId}/order`;
      (await api.put<UpdateOrderRequestBody>(url, order), mutateCache(["/admin/settings/border/ballast_types"]));
    },
    [api],
  );

  const deleteAdminBallastType = useCallback(
    async (ballastTypeId: string): Promise<void> => {
      const url = `/admin/settings/border/ballast_types/${ballastTypeId}`;
      await api.del(url).then(() => {
        mutateCache([`/admin/settings/border/ballast_types`]);
      });
    },
    [api],
  );

  const generateUploadImageUrl = useCallback(
    async (ballastTypeId: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `/admin/settings/border/ballast_types/${ballastTypeId}/image/generate_upload_url?file_name=${encodedFileName}`;
      return api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
    },
    [api],
  );

  return {
    getAdminBallastType,
    useAdminBallastTypes,
    getAdminBallastTypes,
    insertAdminBallastType,
    updateAdminBallastType,
    updateAdminBallastTypeVisibility,
    updateAdminBallastTypeOrder,
    deleteAdminBallastType,
    generateUploadImageUrl,
  };
};
